/**
 * Onboarding Flow Decision Logic
 * 
 * This utility determines which onboarding flow (simplified or full) a user should experience
 * based ONLY on their source data. Invitation-based routing has been removed.
 * 
 * SOURCE PRIORITY SYSTEM (highest to lowest priority):
 * 
 * 1. url_source (NEW SYSTEM) - Primary method
 *    - Sources captured from URL parameters via our new source mapping system
 *    - Already processed and standardized (e.g., url_source: 'SGTech')
 *    - Includes raw_url_source for analytics (e.g., raw_url_source: 'sgtech')
 * 
 * 2. raw_source / source_parameter (INTERMEDIATE)
 *    - Raw URL parameters that need processing through mapSourceToCategory()
 *    - Examples: raw_source: 'sgtech', source_parameter: 'facebook'
 *    - Used when URL capture exists but hasn't been fully processed yet
 * 
 * 3. source (CURRENT SYSTEM) - Dropdown fallback
 *    - Current "Where did you hear about Zavior?" dropdown selection
 *    - Stores backend option ID (e.g., source: 5)
 *    - Defaults to simplified flow (casual users tend to use dropdown)
 * 
 * 4. source_string / source_name (LEGACY SYSTEM) - Backward compatibility
 *    - LEGACY: From older versions of the registration system
 *    - Direct string values (e.g., source_string: "Google Search")
 *    - May exist from data migrations, imports, or previous implementations
 *    - Still processed through new mapping system for consistency
 * 
 * LEGACY SOURCE EXAMPLES:
 * - source_string: "Social Media" (old direct string storage)
 * - source_name: "Referral" (alternative field name from previous versions)
 * - May come from: data imports, API integrations, or system migrations
 * 
 * @author Zavior Team
 * @version 2.0.0 - Source-only routing (removed invitation logic)
 */

import { mapSourceToCategory, isSimplifiedSource, getSourceDescription } from './sourceMapping.js';

/**
 * Determines onboarding flow based ONLY on source data
 * 
 * @param {Object} signupData - User signup data from localStorage
 * @returns {string} 'simplified', 'full' or 'brand'
 *
 * @example
 * determineOnboardingFlow({ code_route: 'brand' }) // Returns 'brand' (backend-resolved code)
 * determineOnboardingFlow({ url_source: 'SGTech' }) // Returns 'simplified'
 * determineOnboardingFlow({ url_source: 'Referral' }) // Returns 'full'
 * determineOnboardingFlow({ source: 12 }) // Returns 'simplified' (fallback)
 */
export const determineOnboardingFlow = (signupData) => {
  console.log('🎯 Starting onboarding flow decision (source-only)...');
  console.log('📊 Signup Data:', signupData);
  
  if (!signupData || typeof signupData !== 'object') {
    console.log('⚠️ No signup data provided, defaulting to simplified flow');
    return 'simplified';
  }

  // Priority 0: Backend-resolved code route (DB-managed signup codes)
  // Set by resolveSignupCode() when the signup URL code exists in the backend.
  // The admin-configured route on the code wins over all local mapping.
  if (['full', 'simplified', 'brand'].includes(signupData.code_route)) {
    console.log(`🗄️ Backend Code Route: ${signupData.signup_code || 'code'} → ${signupData.code_route.toUpperCase()} flow`);
    return signupData.code_route;
  }

  // Priority 1: URL-captured source (from our NEW source mapping system)
  // This is the primary method - sources captured from URL parameters like:
  // /register/signup/SGTech → url_source: 'SGTech', raw_url_source: 'sgtech'
  // Already mapped and standardized by our source capture logic
  if (signupData.url_source) {
    const isSimplified = isSimplifiedSource(signupData.url_source);
    const flowType = isSimplified ? 'simplified' : 'full';
    
    console.log(`🎯 URL Source Decision: ${signupData.url_source} → ${flowType.toUpperCase()} flow`);
    console.log(`📝 Raw URL source: ${signupData.raw_url_source || 'N/A'}`);
    
    return flowType;
  }
  
  // Priority 2: Raw source parameter (direct from URL, needs mapping)
  // For cases where URL parameters are captured but not yet processed through
  // our source mapping system. Examples:
  // - ?source=sgtech (stored as raw_source)
  // - ?utm_source=facebook (stored as source_parameter)
  if (signupData.raw_source || signupData.source_parameter) {
    const rawSource = signupData.raw_source || signupData.source_parameter;
    const mappedSource = mapSourceToCategory(rawSource);
    const isSimplified = isSimplifiedSource(mappedSource);
    const flowType = isSimplified ? 'simplified' : 'full';
    
    console.log(`🔄 Raw Source Mapping: ${rawSource} → ${mappedSource} → ${flowType.toUpperCase()} flow`);
    
    return flowType;
  }
  
  // Priority 3: Dropdown selection (existing system fallback)
  // This is the current "Where did you hear about Zavior?" dropdown in the registration form
  // Stores the selected option ID (e.g., source: 5) from the backend source API
  // We default these to simplified flow since manual selectors are typically casual users
  if (signupData.source) {
    // For dropdown selections, we default to simplified flow
    // since most users who manually select are casual users
    console.log(`📋 Dropdown Source: ID ${signupData.source} → SIMPLIFIED flow (manual selection default)`);
    return 'simplified';
  }
  
  // Priority 4: Legacy source field (backward compatibility)
  // LEGACY SUPPORT: These fields may exist from older versions of the system
  // or different data collection methods. Examples of legacy fields:
  // - source_string: "Google Search" (direct string value from old system)
  // - source_name: "Social Media" (alternative field name from previous versions)
  // We still process these through our new mapping system for consistency
  if (signupData.source_string || signupData.source_name) {
    const sourceString = signupData.source_string || signupData.source_name;
    const mappedSource = mapSourceToCategory(sourceString);
    const isSimplified = isSimplifiedSource(mappedSource);
    const flowType = isSimplified ? 'simplified' : 'full';
    
    console.log(`🔤 Legacy Source: ${sourceString} → ${mappedSource} → ${flowType.toUpperCase()} flow`);
    console.log(`📊 Legacy field used: ${signupData.source_string ? 'source_string' : 'source_name'}`);
    
    return flowType;
  }
  
  // Default: No source data found - default to simplified
  console.log('❓ No source data found, defaulting to SIMPLIFIED flow');
  return 'simplified';
};

/**
 * Get user-friendly description of why a specific flow was chosen
 * 
 * @param {Object} signupData - User signup data
 * @returns {string} Human-readable reason for the flow decision
 * 
 * @example
 * getFlowDecisionReason({ url_source: 'SGTech' })
 * // Returns: "Partner organization (SGTech) - streamlined setup for faster onboarding"
 */
export const getFlowDecisionReason = (signupData) => {
  if (!signupData || typeof signupData !== 'object') {
    return 'No source data available - default streamlined setup';
  }

  if (['full', 'simplified', 'brand'].includes(signupData.code_route)) {
    const label = signupData.url_source || signupData.signup_code || 'Signup code';
    const flowType = signupData.code_route === 'full' ? 'comprehensive' : 'streamlined';
    return `${label} (System > Signup Codes) - ${flowType} setup`;
  }
  
  // URL-captured source
  if (signupData.url_source) {
    const description = getSourceDescription(signupData.url_source);
    const flowType = isSimplifiedSource(signupData.url_source) ? 'streamlined' : 'comprehensive';
    return `${description} - ${flowType} setup for optimal experience`;
  }
  
  // Raw source mapping
  if (signupData.raw_source || signupData.source_parameter) {
    const rawSource = signupData.raw_source || signupData.source_parameter;
    const mappedSource = mapSourceToCategory(rawSource);
    const description = getSourceDescription(mappedSource);
    const flowType = isSimplifiedSource(mappedSource) ? 'streamlined' : 'comprehensive';
    return `Source: ${rawSource} (${description}) - ${flowType} setup`;
  }
  
  // Dropdown selection
  if (signupData.source) {
    return `Manual selection from dropdown - streamlined setup for faster onboarding`;
  }
  
  // Legacy source (backward compatibility with older data formats)
  // These fields might exist from previous versions of the registration system
  // where sources were stored as direct string values instead of IDs
  if (signupData.source_string || signupData.source_name) {
    const sourceString = signupData.source_string || signupData.source_name;
    const mappedSource = mapSourceToCategory(sourceString);
    const description = getSourceDescription(mappedSource);
    const flowType = isSimplifiedSource(mappedSource) ? 'streamlined' : 'comprehensive';
    return `Legacy source: ${sourceString} (${description}) - ${flowType} setup`;
  }
  
  return 'No source identified - default streamlined setup for faster onboarding';
};

/**
 * Track onboarding flow decision for analytics and debugging
 * 
 * @param {string} flowType - The decided flow type ('simplified' or 'full')
 * @param {Object} signupData - User signup data
 * @returns {Object} Tracking data object
 */
export const trackFlowDecision = (flowType, signupData) => {
  const timestamp = new Date().toISOString();
  
  const trackingData = {
    // Core decision data
    flow_type: flowType,
    decision_timestamp: timestamp,
    decision_reason: getFlowDecisionReason(signupData),
    
    // Source analysis
    url_source: signupData?.url_source || null,
    raw_url_source: signupData?.raw_url_source || null,
    dropdown_source_id: signupData?.source || null,
    source_captured_at: signupData?.source_captured_at || null,
    
    // User context
    email_domain: signupData?.email ? signupData.email.split('@')[1] : 'unknown',
    organization_name: signupData?.organization_name || 'unknown',
    country_code: signupData?.countryCode || 'unknown',
    
    // System context
    source_mapping_version: '2.0.0',
    decision_method: 'source_only', // No invitation logic
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    
    // Debug info
    all_signup_data: signupData
  };
  
  // Store in localStorage for potential backend sync
  try {
    localStorage.setItem('onboardingFlowDecision', JSON.stringify(trackingData));
    console.log('📊 Flow Decision Tracked:', trackingData);
  } catch (error) {
    console.warn('⚠️ Failed to store flow decision in localStorage:', error);
  }
  
  // Log for debugging
  console.log('🎯 Flow Decision Summary:');
  console.log(`  • Flow Type: ${flowType.toUpperCase()}`);
  console.log(`  • Primary Source: ${trackingData.url_source || 'None'}`);
  console.log(`  • Reason: ${trackingData.decision_reason}`);
  console.log(`  • Timestamp: ${timestamp}`);
  
  return trackingData;
};

/**
 * Get detailed flow decision analysis for debugging
 * 
 * @param {Object} signupData - User signup data
 * @returns {Object} Detailed analysis of the decision process
 */
export const analyzeFlowDecision = (signupData) => {
  const analysis = {
    signupData: signupData,
    availableSources: [],
    decisionPath: [],
    finalDecision: null,
    confidence: 'high'
  };
  
  // Check all available source types
  if (signupData?.url_source) {
    analysis.availableSources.push({
      type: 'url_source',
      value: signupData.url_source,
      priority: 1,
      mapped: signupData.url_source,
      flow: isSimplifiedSource(signupData.url_source) ? 'simplified' : 'full'
    });
  }
  
  if (signupData?.raw_source || signupData?.source_parameter) {
    const rawSource = signupData.raw_source || signupData.source_parameter;
    const mapped = mapSourceToCategory(rawSource);
    analysis.availableSources.push({
      type: 'raw_source',
      value: rawSource,
      priority: 2,
      mapped: mapped,
      flow: isSimplifiedSource(mapped) ? 'simplified' : 'full'
    });
  }
  
  if (signupData?.source) {
    analysis.availableSources.push({
      type: 'dropdown_selection',
      value: signupData.source,
      priority: 3,
      mapped: 'Manual Selection',
      flow: 'simplified'
    });
  }
  
  // Legacy source fields (backward compatibility)
  // Check for older data formats where sources were stored as strings
  // Common in data imported from previous systems or different collection methods
  if (signupData?.source_string || signupData?.source_name) {
    const sourceString = signupData.source_string || signupData.source_name;
    const mapped = mapSourceToCategory(sourceString);
    analysis.availableSources.push({
      type: 'legacy_source',
      value: sourceString,
      priority: 4,
      mapped: mapped,
      flow: isSimplifiedSource(mapped) ? 'simplified' : 'full'
    });
  }
  
  // Determine decision path
  if (analysis.availableSources.length === 0) {
    analysis.decisionPath.push('No source data found');
    analysis.decisionPath.push('Applying default: simplified flow');
    analysis.finalDecision = 'simplified';
    analysis.confidence = 'low';
  } else {
    // Use highest priority source
    const primarySource = analysis.availableSources[0];
    analysis.decisionPath.push(`Primary source: ${primarySource.type} = "${primarySource.value}"`);
    analysis.decisionPath.push(`Mapped to: ${primarySource.mapped}`);
    analysis.decisionPath.push(`Flow decision: ${primarySource.flow}`);
    analysis.finalDecision = primarySource.flow;
    analysis.confidence = 'high';
  }
  
  return analysis;
};

/**
 * Validate that signup data contains sufficient information for flow decision
 * 
 * @param {Object} signupData - User signup data
 * @returns {Object} Validation result
 */
export const validateSignupDataForDecision = (signupData) => {
  const validation = {
    isValid: false,
    hasSourceData: false,
    warnings: [],
    recommendations: []
  };
  
  if (!signupData || typeof signupData !== 'object') {
    validation.warnings.push('No signup data provided');
    validation.recommendations.push('Ensure signupData is captured and stored in localStorage');
    return validation;
  }
  
  // Check for any source data across all supported formats
  // Includes new system, intermediate, current dropdown, and legacy fields
  const hasSources = !!(
    signupData.url_source ||        // NEW: Processed URL source
    signupData.raw_source ||        // INTERMEDIATE: Raw URL parameter
    signupData.source_parameter ||  // INTERMEDIATE: Alternative raw parameter
    signupData.source ||            // CURRENT: Dropdown selection ID
    signupData.source_string ||     // LEGACY: Direct string value
    signupData.source_name          // LEGACY: Alternative string field
  );
  
  if (hasSources) {
    validation.isValid = true;
    validation.hasSourceData = true;
  } else {
    validation.warnings.push('No source data found in signup data');
    validation.recommendations.push('Implement URL parameter capture in registration form');
    validation.recommendations.push('Ensure source dropdown selection is saved');
  }
  
  // Additional validation
  if (signupData.url_source && !signupData.raw_url_source) {
    validation.warnings.push('URL source found but raw source missing - may affect analytics');
  }
  
  if (!signupData.email) {
    validation.warnings.push('No email found - may affect analytics tracking');
  }
  
  if (!signupData.organization_name) {
    validation.warnings.push('No organization name found - may affect analytics');
  }
  
  return validation;
};

// Browser console helpers for debugging
if (typeof window !== 'undefined') {
  window.onboardingFlowDecision = {
    determineOnboardingFlow,
    getFlowDecisionReason,
    trackFlowDecision,
    analyzeFlowDecision,
    validateSignupDataForDecision
  };
  
  console.log('🎯 Onboarding Flow Decision utilities loaded!');
  console.log('Available in console:');
  console.log('• window.onboardingFlowDecision.determineOnboardingFlow(signupData)');
  console.log('• window.onboardingFlowDecision.analyzeFlowDecision(signupData)');
}
